/**
 * The advisor-firm DIRECTORY document — the data-side replacement for the compiled
 * registry (steward-app#743, docs/32 §6 step 5, docs/26 §Phase 3).
 *
 * A `steward-bg-firm-directory` Lambda (PR 4, `services/firm-directory`) reads appcore's
 * `GET /system/advisor-firms` and publishes one of these documents as a private S3 object.
 * FIVE Lambdas in two stacks read it and hand it to the resolvers in `advisorFirms.ts` as the
 * `directory` half of a {@link FirmLookupContext}; `services/firm-directory/src/index.ts`
 * enumerates them above `PROJECTED_FIELDS`, with the grep the census came from and its date.
 * (Three of the five were named here until 2026-09-10; the reader and the claim mailer were
 * missing.) The count matters to THIS file because the schema below is `strictObject`, so it
 * is the number of artifacts a field addition has to be deployed to before the publisher may
 * emit it.
 * Nothing in this file talks to appcore, to S3, or to AWS at all: it is the SHAPE and the
 * VALIDATION, so that both the producer and every consumer agree on what a firm record is,
 * in one place.
 *
 * ⚠️ **S3, not SSM.** An earlier draft of this header said the publisher writes to SSM, and
 * PR 4 changed it: an SSM ADVANCED parameter caps at 8 KB while `firms` below admits up to
 * 500 records, so the publish would start failing somewhere around the tenth firm — on the
 * day a firm is ONBOARDED, which is the worst available moment for a fleet-wide freeze. The
 * reasoning in full, with the DynamoDB option and why it loses too, is in
 * `deploy/lib/firm-directory-contract.ts`. Nothing about the DOCUMENT changes either way.
 *
 * ## Why the validation lives here and is this strict
 *
 * The compiled registry was trusted because it was *compiled*: a slug in it was written by
 * a reviewer, in a PR, in this repo. Every one of its downstream uses inherited that. Once
 * the same values arrive as a JSON document written by an operator through an ops console,
 * that inheritance is gone and each use has to be re-argued:
 *
 *  - `tenantId` / `firmWorkspaceId` are concatenated into an `execute-api:Invoke` resource
 *    ARN today (`deploy/lib/intake-stack.ts`) and into an STS **session policy** after
 *    #743 PR 8. IAM reads `*` as a wildcard there. That is why {@link isValidAdvisorFirmIdPair}
 *    (strict Crockford, moved here from `deploy/lib/advisor-firm-contract.ts`) runs on every
 *    directory-sourced pair — docs/32 §7 checklist 7, "the same hazard one layer later".
 *  - `slug` reaches a CloudWatch log field. `services/intake-writer` logs it on the explicit
 *    grounds that it is "the CANONICAL slug from the compiled registry"; docs/32 §7 checklist
 *    10 says that argument dies with the registry, so the charset check has to replace it.
 *    A slug that parses here cannot carry a newline, a `%`, or 200 characters.
 *  - `hostnames` drive host→firm resolution, which drives brand injection on an
 *    unauthenticated page and (later) the CORS and Cognito redirect sets. A `*` in one would
 *    widen all three.
 *  - `name` is returned by `advisorFirmNameForMailbox` and LOGGED by
 *    `services/steward-app-api/src/services/inviteThrottle.ts` into a 90-day log group, so
 *    it is charset-bounded too — control characters and newlines are refused.
 *
 * ## Fail closed, wholesale
 *
 * A record that fails validation invalidates the WHOLE document. There is deliberately no
 * "skip the bad row and keep the rest": a partial directory is a directory that silently
 * disagrees with the one the operator wrote, and a firm quietly missing from it is exactly
 * the failure docs/32 is built around (steward-app#1009 — a real family's submission landing
 * nowhere, with nobody told). An unparseable document means "no directory", which the
 * resolvers already answer safely: no firm resolves, the public intake route 404s, the
 * advisor routes return empty.
 *
 * ⚠️ **`strictObject`, so field ADDITIONS are a two-sided rollout.** An unknown key makes the
 * document invalid, on purpose. That includes a `__proto__` key at the document or record
 * level as of zod 4.6.5 (observed 2026-10-05, steward-app#2064; pinned in
 * `advisorFirmDirectory.test.ts`): it is reported as `unrecognized_keys` and
 * `parseAdvisorFirmDirectory` answers null. Under zod 4.4.3 it was silently DROPPED and the
 * document parsed, so do not build a security property on the installed zod's treatment of
 * that one key in either direction; the tests are what notice a change. (Inside a roster
 * entry the key is still dropped, because an entry is `z.object`; see below.)
 * — the same rule `IntakeQueueMessageSchema` states, for the same
 * reason ("a message is not more trusted for having been on our own queue"). The cost is that
 * adding a field to the record means deploying every CONSUMER before the publisher starts
 * emitting it. Say so in the PR that adds one; do not "fix" it by loosening the RECORD.
 *
 * ## The one place that rule is deliberately relaxed, and exactly how far
 *
 * Relaxed on 2026-09-09 with `advisors[]` (steward-app#1343). The DOCUMENT is still
 * `strictObject` and the RECORD is still `strictObject`. What tolerates unknown keys is the
 * INSIDE of one {@link AdvisorFirmRosterEntrySchema} entry, which is `z.object` and therefore
 * STRIPS them — checked against zod 4.4.3 by this session on 2026-09-09 rather than assumed
 * (and the `__proto__` case again against zod 4.6.5 on 2026-10-05, in a test): a
 * `z.object` nested inside a `strictObject` parent drops its own unknown keys, and the parent
 * still rejects the parent's. Every field an entry DOES declare is validated exactly as
 * strictly as anything else in this file, and {@link AdvisorFirmRosterEntrySchema} carries the
 * argument in full.
 *
 * That is a NARROWER claim than the one the paragraph above refuses, which is why the two are
 * not in conflict:
 *
 *  - refusing the RECORD's unknown keys is what makes adding a record field a disciplined,
 *    ordered rollout. Unchanged.
 *  - refusing an ENTRY's unknown keys would buy that same discipline one level further down,
 *    for a sub-object no consumer reads and only appcore writes. **The cost is a fleet-wide
 *    STALE directory, not a fleet-wide `null` one, and an earlier draft of this bullet said
 *    `null`.** Take the case the tolerance exists for: appcore adds a tenth field inside an
 *    entry. Under a strict entry schema the FIRST thing that happens is that the publisher
 *    refuses — it validates with this same schema before it writes anything (`index.ts`,
 *    `AdvisorFirmDirectorySchema.safeParse` → publish NOTHING on failure), so the previous
 *    document stands: stale, alarmed (`FirmDirectoryErrorAlarm`, then `FirmDirectoryStaleAlarm`
 *    at thirty minutes), and visible. Reaching consumer-`null` instead needs a publisher NEWER
 *    than a consumer, which is the ordinary rollout-order hazard the RECORD-level rule above
 *    already covers, not a new one this bullet introduces. Same distinction the `advisors`
 *    uniqueness `superRefine` below draws in full — which side of the publisher a refusal
 *    happens on — and it is worth keeping the two sentences saying it the same way.
 *
 *    Stale-and-alarmed is a milder price than `null`, so the argument for the tolerance is NOT
 *    about severity, and an earlier draft of this bullet leaned on severity. Work both failure
 *    modes through to what actually happens the day appcore adds a tenth key inside an entry:
 *
 *      · **strict entry schema** — the publisher refuses, nothing is written, the previous
 *        document stands. Every firm's directory stalls, fleet-wide, until a consumer deploy
 *        catches up. Loud and alarmed, and everything is stuck;
 *      · **tolerant entry schema** — the publisher parses, drops the unknown key and writes.
 *        Every firm's directory keeps working. The only casualty is that the new key does not
 *        propagate until consumers deploy.
 *
 *    The second is plainly better for a sub-object nothing downstream consumes. So the real
 *    argument is this: **the two-sided rollout discipline is already bought at the RECORD
 *    level, and buying it a second time at the ENTRY level costs a fleet-wide publish stall
 *    for no additional protection.** steward-app#1261 poses that trade at the RECORD level and
 *    leaves it open. Nothing here answers it; this settles the ENTRY level only.
 *
 *    ⚠️ **The tolerance's own cost is SILENCE, and that is the honest objection to it.** The
 *    drop happens in the PUBLISHER, once: `publishDirectory` serializes `parsed.data` and not
 *    the candidate (`services/firm-directory/src/index.ts` step 4, read at source 2026-09-09),
 *    so an unknown entry key never reaches the object at all and no consumer diverges from
 *    another over it. Nothing says so out loud either — a stripped key raises no zod issue, and
 *    the published log line carries a slug list, a firm count and a timestamp, none of which
 *    would show one. A field added upstream simply does not arrive.
 *    That is the failure shape #1261's header warns about elsewhere in this file, and it is
 *    fair to hold it against `z.object` here.
 *
 *    What makes it a considered trade rather than a hole is where the discipline actually
 *    lives. Nothing inside an entry can reach ANY consumer unless `advisors` is named in
 *    `PROJECTED_FIELDS` (`services/firm-directory/src/index.ts`) — and that list is a
 *    hand-written allowlist, so a field arriving is a deliberate act by construction rather
 *    than a default. `advisors` was named there on 2026-09-10 (an earlier version of this
 *    sentence recorded that it was not, as of 2026-09-09; that is now history, not the state).
 *    Since 2026-09-09 the act is guarded in both directions by a PAIR of cases in
 *    `publish.test.ts`: the exact key-set enumeration of a projected record, and "every name in
 *    PROJECTED_FIELDS is a key of the fixture", which is what stops a name being added to the
 *    list with nothing red attached. The second exists because mutation showed the first did
 *    not deliver that on its own (measured 2026-09-09; `project()` copies `field in source`, so
 *    a name with no fixture key changed the published document not at all). Neither case alone
 *    is the guarantee.
 *
 *    ⚠️ Now that `advisors` IS projected, the entry-level tolerance is doing real work rather
 *    than standing in reserve: an unknown key appcore adds inside an entry is dropped by the
 *    publisher on a field that now travels. That is the trade above, taken deliberately. What
 *    it still does not do is decide who may READ a roster entry — see
 *    {@link AdvisorFirmRosterEntrySchema.email} for the two pick-lists that do.
 *
 * 🚨 **This tolerance did NOT buy the `advisors` rollout, and must not be read as if it had.**
 * Every consumer artifact deployed before 2026-09-09 carries a schema in which `advisors` is
 * itself an unknown key on a `strictObject` RECORD. Publishing it before those artifacts are
 * DEPLOYED — not merely merged — nulls the directory fleet-wide, exactly as the paragraph
 * above says. `advisors` therefore shipped in the usual three steps: appcore's schema, then
 * this file together with every consumer artifact (steward-app#1343 step 6, `24cee925`,
 * deployed by Deploy run 34431143738 — both `CDK deploy API stack` and `CDK deploy
 * Intake-writer stack` reported success), and only then `PROJECTED_FIELDS` in
 * `services/firm-directory/src/index.ts`, on 2026-09-10. That run's per-job conclusions were
 * read by the session that took the third step; this file did not take the reading. What the
 * tolerance buys is the next field added INSIDE a roster entry, once the reserved ones declared
 * below are spent.
 */

import { z } from 'zod';

/**
 * Strict Crockford base32 (excludes I, L, O, U). Moved here from
 * `deploy/lib/advisor-firm-contract.ts` in steward-app#743: it stops being only a SYNTH-time
 * check on a deploy input and becomes a RUNTIME check on a directory-sourced id.
 */
const ADVISOR_FIRM_ID_PATTERN = /^[0-9A-HJKMNP-TV-Z]{26}$/;

/**
 * The ONE decision of whether a firm's tenant id + workspace id are well-formed appcore ids.
 *
 * **steward-app#666** is why it is one function and not three: before it existed,
 * `api-stack.ts` (env-var emission — whether the façade thinks the firm is configured) and
 * `intake-stack.ts` (the `execute-api:Invoke` grant — whether the writer can act on it) each
 * wrote their own check and disagreed. `intake-stack.ts` applied this exact strict-Crockford
 * charset while `api-stack.ts` applied none, so a malformed id could configure the public
 * route while the writer silently received no invoke grant, and both deploy jobs reported
 * green. steward-app#743 adds a third caller — the directory parse below — and it must be
 * the same function for the same reason.
 *
 * Strict Crockford, not the looser `[0-9A-Z]` alphabet some seed scripts use for hand-written
 * fixtures: the id is concatenated straight into an `execute-api:Invoke` resource ARN, and
 * after #743 PR 8 into an STS session policy, where IAM reads `*` as a wildcard. This is a
 * security control on that input, not tidiness, and it is deliberately the STRICTER of the
 * two charsets already in use rather than a compromise between them.
 *
 * ⚠️ **THIS IS THE ONLY COPY, and nothing compares it to anything.** Until steward-app#743
 * PR 3 there was a byte-identical mirror in `deploy/lib/advisor-firm-contract.ts`, kept because
 * the CDK validated firm ids as a DEPLOY INPUT and cannot import this package; that file and the
 * two-sided drift test are both deleted. What survives in
 * `deploy/test/intake-infra.test.ts` is ONE-SIDED: it pins this literal
 * (`/^[0-9A-HJKMNP-TV-Z]{26}$/`) and asserts no id validator has reappeared under `deploy/lib`.
 * So loosening the pattern here reds that one test and nothing else — there is no second
 * implementation to disagree with. Do not read the paragraph below as a live guard.
 *
 * *(Why the mirror existed at all, kept because it is why a future contributor may be tempted
 * to recreate one: `cdk.json` runs the CDK app as `ts-node --prefer-ts-exts` (CommonJS) under
 * `deploy/tsconfig.json`, whose `rootDir` is `deploy/` — this package is ESM and outside that
 * root, so a real import breaks `pnpm typecheck` (TS6059) and `cdk synth`. That constraint has
 * not changed. What changed is that `deploy/` no longer takes a firm id as input, so it has
 * nothing to validate and needs no copy.)
 */
export function isValidAdvisorFirmIdPair(tenantId: string, workspaceId: string): boolean {
  return ADVISOR_FIRM_ID_PATTERN.test(tenantId) && ADVISOR_FIRM_ID_PATTERN.test(workspaceId);
}

/**
 * The slug charset. The compiled registry never needed one — a slug there was a TypeScript
 * string literal a reviewer wrote — so this is a new rule rather than a ported one, and it is
 * deliberately the narrowest thing that still names a firm: lowercase letters, digits and
 * hyphens, 1..32 characters.
 *
 * It has to hold for THREE independent reasons, any one of which would justify it:
 * it is a URL path segment (`/intake/{slug}`), it is a CloudWatch log field
 * (docs/32 §7 checklist 10), and it is a cache key and a `Map` key in the façade.
 */
export const ADVISOR_FIRM_SLUG_PATTERN = /^[a-z0-9-]{1,32}$/;

/** True when `slug` is a well-formed firm slug. See {@link ADVISOR_FIRM_SLUG_PATTERN}. */
export function isValidAdvisorFirmSlug(slug: string): boolean {
  return ADVISOR_FIRM_SLUG_PATTERN.test(slug);
}

/**
 * Slugs the intake URL space has already spent, and which therefore cannot name a firm.
 *
 * `/intake/{firmSlug}` is the public intake path, and every STATIC sibling of that
 * `{firmSlug}` segment claims a word a firm can never be given — a firm provisioned with
 * that slug would be handed an intake URL that can never reach its form.
 * `ADVISOR_FIRM_SLUG_PATTERN` admits either word happily, so nothing else in the system
 * would object.
 *
 *  - `resume` — the path the emailed claim link opened (crossmarc/steward-app#1012). The
 *    API routes behind it (`POST /intake/resume`, `/intake/resume/renew`) were removed on
 *    2026-10-07 (crossmarc/steward-app#2158). The word stays reserved for two reasons that
 *    do not depend on those routes: links mailed before then still point at `/intake/resume`,
 *    so a firm given the slug would have those links open its own form; and
 *    appcore mirrors this list by hand (see below), so dropping a word here alone would
 *    make the two lists disagree.
 *  - `firm` — the public firm-identity lookup for the intake page's first paint
 *    (`GET /intake/firm`, `handlers/intakeFirm.ts`). It does not collide with anything
 *    live today: that route is a static `GET` with no `{firmSlug}` sibling method, and the
 *    firm submit path is `POST /intake/{firmSlug}`. Reserved anyway, because the cost is
 *    nothing and the alternative — arguing case by case whether a given fixed segment is
 *    "safe" this week — is exactly what goes stale. The census test below
 *    (`__tests__/reserved-firm-slug-census.test.ts`) parses every fixed segment
 *    `public-router.ts` actually mounts directly under `/intake/` and reds CI the day a new
 *    one is added and not reserved, which is what keeps this list honest rather than
 *    aspirational.
 *
 * Kept as a LIST rather than a single constant because this is a class, not one word: any
 * future static child of `/intake` joins it, and the next person adding one needs a place
 * that already says so.
 *
 * Frozen (`Object.freeze`) because it is read by {@link isReservedAdvisorFirmSlug} on every
 * directory parse and a push/splice from an unrelated caller would be a silent, load-bearing
 * mutation of a security control.
 *
 * ⚠️ **This is steward-app's half of the rule and it is not the whole rule.** The
 * authoritative write of a firm record happens at appcore (`handlers/advisor-firms.ts`,
 * appcore#540, and its repository-level second writer for `seed-scripts/seed-advisor-firm.ts`),
 * which mirrors `resume` and `firm` **by hand** (crossmarc/appcore#545) and enforces
 * the slug charset but knows nothing about steward-app's URL space on its own. A change here
 * does not propagate there. `invitations` was added here on 2026-10-07 (it is the first
 * segment of `GET /intake/invitations/{code}`); as read on that date, appcore's list
 * (`packages/core/src/entities/advisor-firm.ts`) did not carry it. A record written at
 * appcore can therefore carry a reserved slug, and steward-app has to survive reading one.
 *
 * ⚠️ **Enforced in {@link activeDirectoryRecords}, NOT in {@link AdvisorFirmRecordSchema},
 * and the placement is the whole design.** A `.refine()` on the schema's `slug` field is
 * the obvious spelling, and it was the first one written here. It is wrong, because this
 * module's parse fails **WHOLESALE** by deliberate choice (see
 * {@link parseAdvisorFirmDirectory}): one bad record makes the whole document `null`. So a
 * schema-level refusal would turn "an operator provisioned a firm called `resume`" — a
 * record appcore accepts today — into a fleet-wide outage of every firm's intake page,
 * brand injection and CORS/callback set. That is a strictly worse failure than the one the
 * rule exists to prevent, and it would be the FIRST class of record appcore writes and
 * steward-app rejects wholesale; every other wholesale-refusal class (bad charset, bad
 * Crockford id) is already blocked at appcore.
 *
 * Dropping the one record instead gives the semantics the rule actually wants: the
 * offending firm is absent — its intake 404s — and every other firm is untouched. It is the same
 * treatment `status: 'disabled'` already gets, in the same function, for the same reason.
 * Found by this PR's security review; the first draft had the `.refine()`.
 */
export const RESERVED_ADVISOR_FIRM_SLUGS: readonly string[] = Object.freeze(['resume', 'firm', 'invitations']);

/** True when `slug` is well-formed but claimed by the intake URL space. */
export function isReservedAdvisorFirmSlug(slug: string): boolean {
  return RESERVED_ADVISOR_FIRM_SLUGS.includes(slug);
}

/**
 * The companion check to {@link isValidAdvisorFirmSlug}: well-formed AND assignable.
 *
 * Deliberately a SECOND function rather than a tightening of the first. "Is this string a
 * well-formed slug" and "may this string be given to a firm" are different questions with
 * different right answers — a resolver matching an incoming path segment against stored
 * records wants the first, and provisioning wants this one. Folding them together would
 * quietly change the meaning of every existing caller of the charset check.
 */
export function isAssignableAdvisorFirmSlug(slug: string): boolean {
  return isValidAdvisorFirmSlug(slug) && !isReservedAdvisorFirmSlug(slug);
}

/**
 * A hostname as the directory may carry one: lowercase, dotted, no scheme, no port, no path,
 * no userinfo, **no wildcard**. Labels are 1..63 characters of `[a-z0-9-]` that neither start
 * nor end with a hyphen, and there are at least two of them.
 *
 * EXPORTED since steward-app#743 PR 5: the façade's public `GET /intake/firm`
 * (`services/steward-app-api/src/handlers/intakeFirm.ts`) bounds its `?host=` parameter before
 * concatenating it into an outbound URL, and it must bound it to exactly this shape — a host
 * that could never be STORED is a host worth refusing before it becomes a request. It had a
 * byte-identical private copy for one review round; exporting is how the two stop being able
 * to drift.
 *
 * The `*` exclusion is the load-bearing one and it is not hypothetical: a `*` here would reach
 * host→firm resolution (so any host resolves to that firm, and its brand is injected into an
 * unauthenticated page) and, later, the CORS origin and Cognito callback sets. Uppercase is
 * refused rather than lowercased so that "the stored value is the value" holds — the resolvers
 * lowercase the *incoming* host, never the record.
 */
export const ADVISOR_FIRM_HOSTNAME_PATTERN =
  /^(?=.{1,253}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/;

/**
 * An advisor mailbox, stored EXACTLY as it will be matched.
 *
 * ⚠️ **No canonicalisation, and that is the whole point.** `advisorFirmNameForMailbox` is what
 * decides whether an address meets the strict per-recipient invite cap or the generous
 * first-party one (docs/25 checklist 5, docs/32 §7 checklist 6). It matches EXACTLY, on
 * trim+lowercase, and deliberately NOT on the rate limiter's canonical form — widening it to
 * the canonical form "would hand anyone who can guess a firm's domain the generous one"
 * (`advisorFirms.ts`). So the schema REFUSES an address that is not already trimmed and
 * lowercase rather than normalising one: a value that had to be rewritten to match is a value
 * whose stored form and matched form differ, and that difference is where a widening hides.
 */
const ADVISOR_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Text that may reach a log line or an unauthenticated page.
 *
 * Rejects C0/C1 control characters — newline included, which is the one that matters: a name is
 * logged by `inviteThrottle.ts` into a 90-day log group, and a newline there forges a log
 * record. Also rejects the invisible and bidi-override characters (`U+200B`-`U+200F`,
 * `U+2028`-`U+202E`, `U+2060`-`U+2064`, `U+FEFF`), which are not control characters but do the
 * two things this guard exists to stop: `U+202E` visually reverses the consent copy the intake
 * wizard renders verbatim, and `U+2028` survives `JSON.stringify` unescaped into a log line.
 * Neither is a crash; both make a string read as something other than what was stored.
 */
const UNSAFE_TEXT_CHARS =
  /[\u0000-\u001f\u007f-\u009f\u200b-\u200f\u2028-\u202e\u2060-\u2064\ufeff]/;

const logSafeText = (max: number) =>
  z.string().min(1).max(max).refine((s) => !UNSAFE_TEXT_CHARS.test(s), {
    message: 'must not contain control, bidi-override or zero-width characters',
  });

/**
 * ONE advisor mailbox, as {@link AdvisorFirmRecordSchema.advisorEmails} has always validated it.
 *
 * Lifted out of that field's inline chain on 2026-09-09 (steward-app#1343) so that
 * {@link AdvisorFirmRosterEntrySchema.email} and an `advisorEmails` entry cannot drift. They
 * are not two lists that happen to hold addresses: during the transition `advisorEmails` is
 * DERIVED from the roster's `listed` entries, so a rule that held on one side and not the other
 * would let a derived list carry an address its own source would have refused. One decision,
 * one place — the argument {@link isValidAdvisorFirmIdPair} makes for the id pair, applied to
 * the thing that is about to have two call sites.
 *
 * ⚠️ **`.max(254)` before `.regex()`, and the refinements chained on rather than folded into
 * `logSafeText`** — the charset rule here is `ADVISOR_EMAIL_PATTERN`, not a length. `\s` in that
 * pattern blocks CR/LF but NOT NUL, DEL, C1 or a zero-width space, and `String.trim()` does not
 * strip them either, so the trim/lowercase refinement below would happily pass an address
 * carrying one. An advisor mailbox is at least as exposed as a firm NAME: it is a throttle key,
 * an appcore invite recipient, and a stored value a response body returns.
 */
const advisorMailbox = z
  .string()
  .max(254)
  .regex(ADVISOR_EMAIL_PATTERN)
  .refine((e) => !UNSAFE_TEXT_CHARS.test(e), {
    message: 'must not contain control, bidi-override or zero-width characters',
  })
  .refine((e) => e === e.trim().toLowerCase(), {
    message: 'must be stored trimmed and lowercase; the matcher does not canonicalise',
  });

/**
 * The firm's introductory wizard copy, field-for-field the `intro` on `AdvisorFirmDef`.
 *
 * Operator-only (docs/32 §A): it is compliance language from the firm's own counsel, rendered
 * verbatim on an unauthenticated page at the moment a family consents, so it is not something
 * the firm edits through the entity plane. Lengths are bounded because the string is now DATA
 * on a public page rather than a reviewed literal — an unbounded one is a denial-of-service on
 * the intake page and a way to push a wall of text in front of the consent button.
 *
 * `text` is rendered as React children (`IntakeWizard.tsx`), i.e. as text and never as HTML,
 * which is what bounds the injection surface here to layout rather than script. Control
 * characters are refused anyway.
 */
export const AdvisorFirmIntroSchema = z.strictObject({
  subheading: logSafeText(200),
  body: z
    .array(
      z.strictObject({
        kind: z.enum(['lead', 'legal']),
        // Newlines are refused here too: the wizard renders one paragraph per entry, so a
        // paragraph break is a new entry, not a `\n` inside one.
        text: logSafeText(8000),
      }),
    )
    .min(1)
    .max(20),
  startLabel: logSafeText(60),
});

/**
 * What profession a firm practises.
 *
 * ⚠️ **A HAND-MIRROR of appcore's `ADVISOR_FIRM_KINDS`, agreed by review, checked by
 * nothing.** Written 2026-09-07 to match crossmarc/appcore#616; if that list and this one
 * diverge later, no test in either repo goes red. That is the same standing as the
 * `RESERVED_ADVISOR_FIRM_SLUGS` mirror above and WEAKER than
 * `ADVISOR_FIRM_ID_PATTERN`'s, which `deploy/test/intake-infra.test.ts` pins by reading
 * the source file as text. Do not read "mirrors appcore" as "verified against appcore".
 *
 * Divergence is deliberately cheap here, and {@link AdvisorFirmRecordSchema.kind} is why:
 * the stored field is a bounded STRING, not this enum, so a firm whose record carries a
 * word this list has not heard of resolves normally and simply renders unlabelled. This
 * constant is the DISPLAY vocabulary, not a validation gate.
 *
 * ⚠️ **A LABEL, and nothing in this repo may branch on it for authorization.** docs/26
 * §"'Different types' is a defaults question, not a mechanism" is explicit: advisor kind
 * "belongs in the firm profile as a label that drives a **suggested default scope set** at
 * connect time, which the family then edits. It must stay a suggestion: the family decides,
 * and a 'kind' that silently granted areas would invert Phase 5's whole point." The same
 * ruling forbids a hierarchy between kinds and forbids per-kind role ids — composing pillar
 * roles onto the membership already expresses every combination. The consumer that reads
 * this to pick a SUGGESTED default is steward-app#1262 and does not exist yet.
 *
 * ## These are `ContactCategory` minus `doctor`, and that is a rule rather than a list
 *
 * `src/api/types.ts`'s `ContactCategory` is where this codebase already enumerates
 * professions, for the family's Contacts tab (a tab of the People pillar since
 * steward-app#1425, and its own pillar before that). The two vocabularies name the same
 * real-world thing from two sides — a family records its CPA as a `contact` today and may
 * connect that same CPA's firm tomorrow — so they must not drift into synonyms. `doctor` is
 * the one category that never names a firm a family engages to see its financial picture;
 * every other one plausibly does, `estate` included.
 *
 * `advisorFirmKinds.test.ts` asserts the subset in CI, in the form
 * `new Set([...ADVISOR_FIRM_KINDS, 'doctor']) === new Set(ContactCategory)` — which reds
 * both when a Contacts category is added without a decision here AND when a firm kind is
 * invented that Contacts has no word for.
 */
export const ADVISOR_FIRM_KINDS = [
  'lawyer', 'accountant', 'financial-advisor', 'insurance', 'estate', 'other',
] as const;

export type AdvisorFirmKind = (typeof ADVISOR_FIRM_KINDS)[number];

/**
 * True when a stored `kind` is one this build knows how to label.
 *
 * The companion to storing `kind` as a bounded string rather than an enum: the SCHEMA lets
 * an unrecognised value through so one record cannot null the whole directory, and this is
 * where the vocabulary is actually applied. A display surface calls this and falls back to
 * rendering nothing rather than showing a raw slug it has no label for.
 *
 * Deliberately a predicate rather than a coercion. "Is this a kind I can label?" and "what
 * is this firm's kind?" are different questions, and a function that quietly mapped an
 * unknown value to `'other'` would make a firm look like it had declared something it
 * never did.
 */
export function isAdvisorFirmKind(kind: string | undefined): kind is AdvisorFirmKind {
  return kind !== undefined && (ADVISOR_FIRM_KINDS as readonly string[]).includes(kind);
}

/**
 * A firm's own word for its intake form, singular and plural.
 *
 * "Financial Snapshot" is RIPL's name for its own questionnaire, not a Crossmarc product
 * noun. docs/25's naming rule already says so ("UI copy only; identifiers say intake") and
 * the IDENTIFIERS held that line — every route, `typeCode` and queue field says `intake`.
 * The COPY did not: the phrase is hard-coded across the public wizard, the advisor portal
 * that every firm sees, and the footer of the PDF a firm hands its own client. docs/26
 * §Terminology already conceded the point ("the nav label is ours only until a firm's word
 * is configurable, and it should be"). This field is that.
 *
 * Both forms are stored rather than one derived from the other. English plurals are not a
 * function you can write — "Financial Snapshot" pluralises by rule, "Your Financial Review"
 * and "Client Intake" do not reliably — and a firm's own name for its own document, wrong,
 * on its own PDF, is exactly the error this exists to prevent.
 *
 * ⚠️ **Absent must fall back to HOUSE-NEUTRAL copy, never to "Financial Snapshot."** A
 * default carrying one firm's brand means the next firm inherits it by omission, which is
 * the present defect wearing a config file. `intakeNoun()` in `src/intake/intakeNoun.ts`
 * owns that fallback for the SPA, and `renderClaimEmail()` in
 * `services/steward-app-api/src/services/claimEmail.ts` owns it for the façade's claim
 * mail; there is deliberately one such function per side rather than a `??` at each call
 * site, and both import the SAME {@link HOUSE_INTAKE_NOUN} below rather than each keeping
 * its own copy of the word.
 */
export const AdvisorFirmIntakeLabelSchema = z.strictObject({
  /** e.g. "Financial Snapshot". Rendered mid-sentence, so stored cased as it should read. */
  singular: logSafeText(60),
  /** e.g. "Financial Snapshots". Not derived from `singular` — see above. */
  plural: logSafeText(60),
});

export type AdvisorFirmIntakeLabel = z.infer<typeof AdvisorFirmIntakeLabelSchema>;

/**
 * The house-neutral fallback (Matt's 2026-09-19 decision moved the word itself to
 * "financial snapshot", steward-app#1262). Defined ONCE, here, because both deploy
 * artifacts that need it — the SPA (`src/intake/intakeNoun.ts`) and the façade
 * (`services/steward-app-api/src/services/claimEmail.ts`) — cannot import from each other
 * (steward-app#319: they deploy separately, and there is no dependency edge between them),
 * but both already depend on this package for `AdvisorFirmIntakeLabel` itself. A per-side
 * hand-kept copy of a user-facing word is exactly the drift this shared package exists to
 * prevent, and the moment it actually diverges is the next rename, by someone who greps
 * for the word, finds one of the two copies, and ships a half-rename. `intakeNoun.test.ts`
 * on both sides scans for the literal string outside this one definition, so that failure
 * mode goes red instead of shipping quietly.
 */
export const HOUSE_INTAKE_NOUN: AdvisorFirmIntakeLabel = {
  singular: 'financial snapshot',
  plural: 'financial snapshots',
};

/**
 * A bounded, log-safe, NON-BLANK string — every free-text field on a roster entry.
 *
 * `logSafeText` on its own admits `'   '`: its `.min(1)` counts characters before any trim, so
 * a whitespace-only value passes a check whose whole purpose is "there is a value here". That
 * is harmless for a field nothing renders and wrong for every field that ever gets rendered,
 * and which of those a reserved field turns out to be is not knowable today.
 *
 * ⚠️ **One helper rather than the rule repeated six times, added 2026-09-09.** `name` carried
 * the refinement inline first and the five reserved fields did not, which made "does this field
 * refuse a blank?" a per-field fact a reader had to check one declaration at a time — and a
 * seventh field would have inherited whichever neighbour its author copied. As a helper the
 * answer is the same for all of them by construction. `advisorFirmDirectory.test.ts` enumerates
 * the schema's string fields and asserts the rule holds for every one, so a seventh field
 * declared with bare `logSafeText` reds rather than depending on the next author noticing.
 *
 * ⚠️ **Deliberately NOT folded into `logSafeText` itself.** That helper is also used by the
 * RECORD's `name`, `kind`, `kindLabel`, `intro` and `intakeLabel` — fields that DO have
 * producers and published values — so tightening it there is a migration with a rollout, not a
 * one-line edit. This one was free because no firm had a published roster when it was written
 * (checked in all three environments on 2026-09-10). ⚠️ It stops being free the moment an
 * operator seeds one — appcore's `seed-advisor-firm` (`FIRM_ADVISORS`) and
 * `PUT /admin/advisor-firms/{tenantId}` both write `advisors` as of appcore PR #641 — because
 * a stored value that fails a tightened rule refuses the PUBLISH fleet-wide.
 */
const rosterText = (max: number) =>
  logSafeText(max).refine((s) => s.trim().length > 0, {
    message: 'must not be blank after trimming',
  });

/**
 * The body of `PUT /advisor/firm` (steward-app#1730): a firm admin renames their firm. A
 * `strictObject` with the one key, so any other field is a 400 rather than silently dropped.
 *
 * ⚠️ **A HAND-MIRROR of appcore's `AdvisorFirmNameUpdateSchema`** (`packages/core/src/
 * entities/advisor-firm.ts`, read at appcore `44a604f`, crossmarc/appcore#730, on 2026-09-23):
 * `strictObject({ name: rosterText(120) })`, and appcore's `rosterText` and `UNSAFE_TEXT_CHARS`
 * were byte-compared with this file's on the same day. The façade parses with it so it never
 * forwards a name appcore's member route would refuse, and never writes half of the two-step
 * rename (the directory name, then the workspace name) on a name the other half would refuse.
 * Nothing checks the two copies against each other. Also STRICTER than the directory reader's
 * record `name` (`logSafeText(120)`, no blank rule), which is the safe direction: every name
 * this admits, the reader accepts. `advisorFirmDirectory.test.ts` pins both.
 */
export const AdvisorFirmNameUpdateSchema = z.strictObject({
  name: rosterText(120),
});
export type AdvisorFirmNameUpdate = z.infer<typeof AdvisorFirmNameUpdateSchema>;

/**
 * ONE advisor on a firm's published roster (steward-app#1343, `advisors[]`, 2026-09-09).
 *
 * The unit of the advisor relationship is the ADVISOR, not the firm: a family connects to a
 * person who happens to practise at a firm. This entry is the directory's half of that, and the
 * roster it lives on is what {@link AdvisorFirmRecordSchema.advisorEmails} is derived from
 * during the transition.
 *
 * ## 🚨 `z.object`, and it is the only non-strict object in this file
 *
 * Unknown keys inside an entry are **tolerated and DROPPED** — `z.object` strips, checked
 * against zod 4.4.3 by this session on 2026-09-09 rather than assumed. Read this file's header
 * for the whole argument; the short form is that the ENTRY is a sub-object no consumer reads
 * and only appcore writes, so refusing its unknown keys would buy rollout discipline at the
 * price of a publish that refuses fleet-wide the day appcore adds a field — the previous
 * document standing, stale and alarmed. (**Stale, not `null`.** The publisher validates with
 * this schema before writing, so it is the publish that stops, not every consumer's parse.
 * Consumer-`null` is the rollout-ORDER hazard, and it belongs to the strict RECORD around this
 * entry, which is unchanged.)
 *
 * ⚠️ **What that buys, and what it costs, in one line each.** It buys a directory that keeps
 * publishing on the day appcore adds a key here, instead of one that stalls fleet-wide until
 * every consumer redeploys. It costs silence: the key is dropped by the publisher with no zod
 * issue and no log line, so a field added upstream just does not arrive. `advisors` IS named in
 * `PROJECTED_FIELDS` as of 2026-09-10, so that silence now applies to a field that really
 * travels; what keeps it bounded is that `publish.test.ts` guards every change to that list in
 * both directions, and that an entry's declared fields are validated strictly. The header
 * carries the full trade; do not restate it here, and do not weaken it to "unknown keys are
 * harmless".
 *
 * ⚠️ **This is tolerance about UNKNOWN keys and nothing else.** Every key declared below is
 * validated exactly as strictly as anything else here, and `advisorId`, `email`, `name` and
 * `listed` are all REQUIRED: an entry missing one is refused, and refusing it fails the record,
 * the document and therefore the whole directory. That is the intended direction — a roster
 * entry with no id or no mailbox is not a degraded advisor, it is a malformed write that
 * appcore's `z.strictObject` on the same shape should have refused before it was ever stored.
 *
 * ## The reserved fields are declared NOW, on purpose
 *
 * `title`, `credentials`, `location`, `bio` and `photo` have **no producer** — nothing writes
 * them and nothing renders them as of 2026-09-09. They are declared anyway because #1343 says
 * the advisor card should "grow without a reset", and under a strict RECORD that sentence is
 * only true if the fields already parse: adding one later would cost a full two-sided rollout
 * (appcore, then every consumer artifact, then `PROJECTED_FIELDS`) per field. Declaring them
 * costs a bound each and makes the claim real instead of aspirational.
 *
 * ⚠️ **Every string is bounded, and the bounds are the cost control.** The directory is read by
 * a full-table Scan on appcore's side and by every consumer on a five-minute cache, so a roster
 * enlarges a record that is already re-read constantly. The Phase B assessment of 2026-09-09
 * is that 50 advisors at these bounds adds single-digit KB per record and is negligible against
 * that scan — but the conclusion depends on the bounds existing, and an unbounded `bio` is what
 * would make it false.
 *
 * ⚠️ **The RCU figure that assessment rests on is INHERITED, not measured here.** It is
 * ~1,351 RCU/call for `AdvisorFirmRepository.listAll`, attributed to `advisor-firms.ts:47-58`
 * on `appcore-dev`, and it reached this file through the Phase B field spec rather than from a
 * reading this repo took. It is a point-in-time number about a table this repo cannot see, so
 * treat it as a claim with a date on it. The part that does NOT expire is the shape of the
 * argument: a Scan re-reads every byte of every record, so per-record growth is the thing to
 * bound, whatever the current figure is.
 */
export const AdvisorFirmRosterEntrySchema = z.object({
  /**
   * appcore's id for the advisor. Strict Crockford, the same 26-character shape as
   * {@link AdvisorFirmRecordSchema.firmWorkspaceId} — see {@link ADVISOR_FIRM_ID_PATTERN} for
   * why that charset and not a looser one.
   *
   * The connect target and the deep-link key, which is why {@link AdvisorFirmRecordSchema} also
   * refuses a roster that repeats one.
   */
  advisorId: z.string().regex(ADVISOR_FIRM_ID_PATTERN),
  /**
   * The advisor's mailbox, stored exactly as it will be matched. {@link advisorMailbox} — the
   * SAME schema object an `advisorEmails` entry uses, so the derived list and its source cannot
   * validate differently.
   *
   * 🚨 **NEVER family-reachable, and as of 2026-09-10 there is exactly ONE barrier left.**
   * Both of the others were spent by steward-app#1343, on purpose, two steps apart:
   *
   *   - the PUBLISHER's `PROJECTED_FIELDS` (`services/firm-directory/src/index.ts`) names
   *     `advisors`, so this address IS in the published directory document. That document is
   *     private to the fleet and read only by Lambdas through the SDK;
   *   - `firmFromRecord` (`advisorFirms.ts`) now COPIES the roster onto the resolved
   *     {@link AdvisorFirm}, which is the façade-only hydrated type `advisorEmails` and
   *     `tenantId` already live on. Step 7 needed it: the façade cannot resolve a connect
   *     request's `advisorId` to one server-side address without the roster in hand.
   *
   * ⚠️ **"ONE barrier" still holds after steward-app#1364, and the number that moved is the
   * one below it.** A barrier is a layer this address has to get through; the two spent above
   * are gone and no third was spent. What #1364 changed is how many hand-written projections
   * stand INSIDE that last barrier: two became three. Do not read the count below as a count of
   * barriers, and do not "reconcile" the two figures — they are counting different things.
   *
   * What remains is THREE hand-written projections in
   * `services/steward-app-api/src/handlers/advisor.ts`. This paragraph named only the first
   * until steward-app#1365/#1366 added the third field to the second, and only two until
   * steward-app#1364 added the third (2026-09-10, re-read at source by that branch rather than
   * inherited):
   *
   *   - `offeredAdvisors`, the `items` pick list on `GET /advisor/firms` — `advisorId`, `name`
   *     and `title`, filtered to `listed` entries whose address is in `advisorEmails`. This is
   *     the BROWSE path: it goes to any authenticated family user, for a firm they have no
   *     relationship with, which is why it is the stricter of the two;
   *   - `foldAdvisorPeople`, the `advisors[]` rows on `GET /advisor/connections` — the same
   *     three fields since #1365, hung off an address the response already carries, for a firm
   *     this family is CONNECTED to, and gated on the address being one the server put in the
   *     set rather than one the caller planted. It is deliberately wider than `listed` (an
   *     unlisted person whose address is still in `advisorEmails` keeps their row and their
   *     name) and it is not a connect path: `resolveConnectTarget` re-asks `offerableAdvisors`
   *     for every id, so an id served there resolves to a 404.
   *   - `resolveOffer`, the single `offer` on `GET /advisor/offers/{advisorId}`
   *     (steward-app#1364) — `advisorId`, `name` and `title` off the entry, plus the firm's own
   *     `firmId` and `name`, for the ONE entry an opaque id names. It is on the BROWSE path and
   *     therefore carries `offeredAdvisors`' strictness, not `foldAdvisorPeople`'s: it asks the
   *     same `offerableAdvisors` predicate, so an unlisted entry, or one whose address is
   *     outside `advisorEmails`, resolves to nothing. It is WIDER than `offeredAdvisors` in one
   *     respect and narrower in every other: wider because it answers for an `internal` firm,
   *     which the `items` offer filter drops (that is the whole of #1364 — an advisor at the
   *     demo firm was handed a connect link nothing could resolve); narrower because it answers
   *     for one id at a time, only when the caller already holds it, and has no listing form.
   *
   * ⚠️ **A projection added to this file's `AdvisorFirmRosterEntry` reaches all three.** That
   * is why the reserved fields (`credentials`, `location`, `bio`, `photo`) are safe only for as
   * long as every one of the three stays a PICK LIST: not one of them may become a spread of the
   * entry minus a key, or the day appcore's writer populates a reserved field is the day all
   * three start shipping it.
   *
   * ⚠️ **The property is about THIS FIELD, and it is not "no family ever sees an advisor's
   * address"** — an earlier draft of this paragraph said that and it is plainly false (found by
   * the review of the #1365/#1366 branch, on the branch's own new prose). A CONNECTED family has
   * always been able to see these addresses: they are on `GET /advisor/connections` as
   * `attributes.advisorEmails`, and `foldAdvisorPeople`'s rows are keyed by them. The claim that
   * holds, and the one this schema is defending, is narrower and is about the BROWSE path:
   * `offeredAdvisors` never ships a roster entry's `email` to a family with no relationship to
   * the firm, and `foldAdvisorPeople` never introduces a roster address the connection did not
   * already carry (it is a LOOKUP over `advisorEmailSet`, so an entry at an address in neither
   * half produces no row at all).
   *
   * Each is covered by tests that read the SERIALIZED response body rather than a parsed key
   * set — `__tests__/advisorConnectByAdvisorId.test.ts` covers the first two against a literal
   * registry (including "never carries a roster address the connection does not already have"),
   * `__tests__/advisorFirmDirectory.test.ts` covers the first against the REAL directory parser,
   * and `__tests__/advisorLinkedOffer.test.ts` covers the third across seven ids and six
   * addresses, on the resolving path and on every refusing one. Widen any of the three and an
   * address is in a stranger's browser.
   *
   * Read as a claim with a date: the first was verified at source and by running both suites on
   * 2026-09-10; the second was added to this list by the #1365/#1366 branch the same day, which
   * re-read `foldAdvisorPeople` rather than inheriting the count; the third by the #1364 branch,
   * also 2026-09-10, after an independent adversarial pass caught that this census said TWO
   * while that branch was adding a third and cross-referencing this very paragraph as the
   * enumeration of record. A census is a point-in-time reading. **Grep
   * `handlers/advisor.ts` for the roster fields rather than trusting this list**: it has been
   * one short twice in two days, and both times the branch that made it short is the one that
   * cited it.
   */
  email: advisorMailbox,
  /**
   * Display name. Logged wherever a firm name is logged, so control, bidi-override and
   * zero-width characters are refused, and a name that is only whitespace is refused too. See
   * {@link rosterText} — the refinement was inline here until 2026-09-09 and is now shared with
   * every other free-text field on the entry.
   */
  name: rosterText(120),
  /**
   * Whether this advisor is offered to families.
   *
   * REQUIRED and not defaulted. A default would decide, in a schema, whether a person appears
   * in front of families — and it would decide it silently for every entry a future writer
   * forgot to set. The writer says yes or no.
   *
   * During the transition {@link AdvisorFirmRecordSchema.advisorEmails} is derived from the
   * `listed: true` entries, in entry order.
   */
  listed: z.boolean(),
  /**
   * The advisor's job title, as the firm words it. "Wealth Advisor", "Partner".
   *
   * ⚠️ **NO PRODUCER, but it now HAS A CONSUMER, and it is a family** (steward-app#1343 step 7,
   * 2026-09-10). This field used to read "Reserved. No producer, no consumer" alongside the
   * four below it, and only half of that is still true. The façade's `items` projection
   * (`offeredAdvisors` in `services/steward-app-api/src/handlers/advisor.ts`) ships
   * `advisorId`, `name` and `title` to every authenticated family user so they can tell two
   * advisors at one firm apart. `credentials`, `location`, `bio` and `photo` are NOT shipped
   * and remain reserved in both senses.
   *
   * That crossing is the widening `services/firm-directory/src/index.ts`'s header asks to be
   * judged deliberately: a roster entry carries free text about a real person, and `name` and
   * `title` are the two the firm writes precisely so a client can recognise them. The other
   * four are not, and adding one to the projection is a fresh decision, not a precedent this
   * sets.
   *
   * ⚠️ **`rosterText`, so `undefined` is fine and `'   '` is not**, tightened 2026-09-09 along
   * with the four fields below it. It was bare `logSafeText` for one round, which meant these
   * five accepted a blank string while `name` did not — an asymmetry inside one object, held
   * safe only by the writer happening not to emit one. Done NOW because nothing writes these
   * fields: no published document can carry a blank one, so there is nothing to migrate. Once
   * a producer exists the same edit needs a rollout.
   */
  title: rosterText(80).optional(),
  /** Reserved. No producer, no consumer, as of 2026-09-09. See {@link rosterText}. */
  credentials: rosterText(120).optional(),
  /** Reserved. No producer, no consumer, as of 2026-09-09. See {@link rosterText}. */
  location: rosterText(120).optional(),
  /** Reserved. No producer, no consumer, as of 2026-09-09. See {@link rosterText}. */
  bio: rosterText(600).optional(),
  /**
   * Reserved. No producer, no consumer, as of 2026-09-09.
   *
   * ⚠️ A bounded, charset-checked STRING and deliberately not a validated URL: nothing renders
   * it, so a URL rule written now would be a guess about a surface that does not exist. The
   * first change that renders it owns that validation, and must not assume this bound is one.
   *
   * ⚠️ **`rosterText` like the other four, and the case for treating it differently was
   * considered and rejected.** It is the field least like a label — a path or a URL rather than
   * prose — so "must not be blank" is arguably a rendering concern here rather than a data one.
   * It gets the rule anyway for two reasons. A present-but-whitespace photo reference is not a
   * value under any reading of the field, so nothing is lost. And an exception would have to be
   * written down twice: once here, and once as a skip in the test that enumerates this schema's
   * string fields — which would put back exactly the per-field memory this helper removes.
   */
  photo: rosterText(512).optional(),
});

export type AdvisorFirmRosterEntry = z.infer<typeof AdvisorFirmRosterEntrySchema>;

/**
 * One firm, as the directory carries it.
 *
 * **What is deliberately NOT here**, each because it has an authoritative home elsewhere and a
 * second copy is a drift surface (docs/32 §A):
 *  - `notifyEmail` — no consumer exists; a mailbox in a published artifact for a consumer that
 *    does not exist yet is a leak with no benefit.
 *  - the assurance FLOOR policy — appcore owns it, with its own operator route.
 *  - the workspace AUTH MODE — CDK-owned in crossmarc-auth; a console-side write drifts away on
 *    the next deploy.
 *
 * `recordVersion` is appcore's `_v` on the underlying `CFG#advisor-firm` item. It travels with
 * the resolved firm, is stamped into the intake queue message (v2, `intakeMessage.ts`), and is
 * what lets the writer compare *façade-time directory* against *writer-time directory* — the
 * one comparison that does not become vacuous once both sides resolve from the same source.
 */
export const AdvisorFirmRecordSchema = z.strictObject({
  /**
   * URL slug and directory key. See {@link ADVISOR_FIRM_SLUG_PATTERN}.
   *
   * ⚠️ **The reserved-slug rule is deliberately NOT enforced here** — it is applied in
   * {@link activeDirectoryRecords} instead. Putting a `.refine()` on this field is the
   * obvious spelling and it is the wrong one: this schema fails **wholesale**, so one
   * firm record carrying a reserved slug would null the ENTIRE directory and take every
   * other firm's intake page, brand and invite throttle down with it. See
   * {@link RESERVED_ADVISOR_FIRM_SLUGS}.
   */
  slug: z.string().regex(ADVISOR_FIRM_SLUG_PATTERN),
  /** Display name. Logged by the invite throttle, so control characters are refused. */
  name: logSafeText(120),
  /** appcore tenant id of the firm. Strict Crockford — see {@link isValidAdvisorFirmIdPair}. */
  tenantId: z.string().regex(ADVISOR_FIRM_ID_PATTERN),
  /** The firm's own workspace, where intake submissions live. Strict Crockford. */
  firmWorkspaceId: z.string().regex(ADVISOR_FIRM_ID_PATTERN),
  /** Branded hosts. May be empty: a firm can be authorized for intake with no branded host. */
  hostnames: z.array(z.string().regex(ADVISOR_FIRM_HOSTNAME_PATTERN)).max(20),
  /** Brand preset key. An unknown key renders unbranded, which is a real degradation. */
  brandKey: z.string().regex(ADVISOR_FIRM_SLUG_PATTERN),
  /** Advisor mailboxes, stored exactly as matched. See {@link ADVISOR_EMAIL_PATTERN}. */
  advisorEmails: z
    // ⚠️ **The SAME `advisorMailbox` object `advisors[].email` uses**, not a second copy of its
    // rules — see that const for why, and note that this is the one thing keeping a DERIVED
    // `advisorEmails` and the roster it is derived from validating identically.
    .array(advisorMailbox)
    // ⚠️ This cap is now the ONLY thing bounding the invite fan-out on connect.
    // `handlers/advisor.ts` justifies its uncounted per-request appcore writes on the grounds
    // that "the count per request is `firm.advisorEmails.length`, a compiled constant of this
    // service rather than anything a caller sizes". Once the list comes from a record, that
    // sentence is only true because of this number. 50 is generous for a real firm; #743 PR 6
    // should decide whether it wants to be tighter when it wires the directory into that path.
    .max(50),
  /**
   * The firm's advisor roster. See {@link AdvisorFirmRosterEntrySchema}.
   *
   * **OPTIONAL, and it stays optional for the whole transition.** Every record published before
   * 2026-09-09 has none, and absent means absent rather than `[]` — a firm whose roster has
   * never been written is a different state from one whose roster was emptied, and only the
   * writer knows which.
   *
   * ⚠️ **Published since 2026-09-10, and READ since the same day.** `PROJECTED_FIELDS`
   * (`services/firm-directory/src/index.ts`) copies it into the directory document, and
   * `firmFromRecord` (`advisorFirms.ts`) copies it onto a resolved {@link AdvisorFirm} — the
   * façade-only hydrated type, never the SPA-safe `AdvisorFirmDef`. steward-app#1343 step 7
   * needed that: `POST /advisor/connections` takes an `advisorId` and resolves it to exactly one
   * address against this roster, server-side.
   *
   * With those two allowlists spent, the ONE remaining barrier between an address here and a
   * family's browser is the façade's `items` projection (`offeredAdvisors` in
   * `handlers/advisor.ts`), which picks `advisorId`, `name` and `title` and filters out entries
   * that are not `listed`. See {@link AdvisorFirmRosterEntrySchema.email}.
   *
   * ⚠️ **`.max(50)` for the reason `advisorEmails` carries the same number**: the roster is what
   * `advisorEmails` is derived from, so anything that bounds the invite fan-out has to bound
   * this too or the bound moves one hop upstream and disappears.
   */
  advisors: z
    .array(AdvisorFirmRosterEntrySchema)
    .max(50)
    /**
     * Two entries may not share an `advisorId`, and two may not share an `email`.
     *
     * On the ARRAY rather than in a handler because there is no single handler: the id is the
     * connect target and the deep-link key, and a duplicated mailbox would fan one invitation
     * out twice. A rule enforced at each point of use is a rule that is missing at the point
     * added last.
     *
     * ⚠️ **Refusing here is fail-CLOSED, not fail-fleet, and the difference is which side of
     * the publisher it happens on.** The `services/firm-directory` publisher validates with
     * THIS schema before it writes anything (`index.ts`, `AdvisorFirmDirectorySchema.safeParse`
     * → publish NOTHING on failure), so a duplicate-carrying record refuses the PUBLISH and
     * leaves the previous document in place: stale, alarmed (`FirmDirectoryErrorAlarm`, then
     * `FirmDirectoryStaleAlarm` at thirty minutes), and visible. It is not the consumer-side
     * `null` that steward-app#1261 is about, because a document carrying the duplicate never
     * gets written for a consumer to choke on.
     *
     * Comparing `email` raw is comparing it post-normalisation: {@link advisorMailbox} refuses
     * an address that is not already trimmed and lowercase, so the two forms are the same
     * string by the time this runs. Pinned in both directions in this file's tests.
     */
    .superRefine((entries, ctx) => {
      const seenIds = new Set<string>();
      const seenEmails = new Set<string>();
      entries.forEach((entry, index) => {
        // PATH AND CODE ONLY, no value in the message: the publisher logs a refusal's issue
        // paths into a 90-day cross-firm log group, and these values are real mailboxes.
        if (seenIds.has(entry.advisorId)) {
          ctx.addIssue({ code: 'custom', path: [index, 'advisorId'], message: 'duplicate advisorId' });
        }
        seenIds.add(entry.advisorId);
        if (seenEmails.has(entry.email)) {
          ctx.addIssue({ code: 'custom', path: [index, 'email'], message: 'duplicate email' });
        }
        seenEmails.add(entry.email);
      });
    })
    .optional(),
  /** Crossmarc's own demo/internal firm. Resolves normally; the SPA-facing filter is elsewhere. */
  internal: z.boolean(),
  /**
   * `disabled` resolves as ABSENT, never as a firm carrying a status.
   *
   * ⚠️ As of 2026-10-08 this is NOT only a reversible switch for the readers above. The kept
   * remover (`services/kept-remover`) treats `disabled` as an instruction to end every kept bank
   * connection of that firm's clients at Plaid and delete the rows, at its next daily sweep.
   * That cannot be undone: setting the firm back to `active` restores nothing. There is no
   * grace period.
   */
  status: z.enum(['active', 'disabled']),
  /**
   * What profession the firm practises. A label, never an authorization input. Optional:
   * every firm registered before this field existed has none, and absent means absent
   * rather than a defaulted `financial-advisor`, which would be a guess written into data.
   *
   * 🚨 **A bounded STRING and deliberately NOT `z.enum(ADVISOR_FIRM_KINDS)`, and the
   * difference is a fleet-wide outage.** This document is refused **wholesale** — one
   * unparseable record nulls the entire directory, and every firm's intake page, brand
   * injection and invite throttle go with it (see this file's header, and
   * {@link RESERVED_ADVISOR_FIRM_SLUGS} for the same argument reaching the same conclusion
   * about a different trigger). A closed enum here would mean that the day appcore ships a
   * seventh kind, or an operator writes `cpa` where this list says `accountant`, ONE firm's
   * optional display label takes down the advisor channel for EVERY firm.
   *
   * That is the worst possible failure for the least important field on the record, and it
   * is precisely the trade the reserved-slug rule already refused to make. So the schema
   * bounds the string and nothing more; {@link ADVISOR_FIRM_KINDS} is applied where the
   * value is DISPLAYED, and an unrecognised kind renders unlabelled instead of exploding.
   * Raised by the code review of steward-app#1264; tracked in the general case as #1261.
   */
  kind: logSafeText(40).optional(),
  /** The firm's own words for its {@link AdvisorFirmRecordSchema.kind}. Display only. */
  kindLabel: logSafeText(40).optional(),
  intro: AdvisorFirmIntroSchema.optional(),
  /** The firm's own word for its intake form. See {@link AdvisorFirmIntakeLabelSchema}. */
  intakeLabel: AdvisorFirmIntakeLabelSchema.optional(),
  /** appcore's `_v` on the firm record. Monotonic per record. */
  recordVersion: z.number().int().min(0),
})
  // The id check runs as the FUNCTION, not as a second copy of its pattern.
  //
  // Both fields above already carry `ADVISOR_FIRM_ID_PATTERN`, so this is redundant today and
  // that is the point: steward-app#666's whole lesson is that "one decision, one function" is
  // what stops two call sites from drifting, and a reader who greps the callers of
  // `isValidAdvisorFirmIdPair` to see what a change to it affects must find the directory
  // among them. Without this the header's claim that it "runs on every directory-sourced pair"
  // is prose, not code.
  .refine((r) => isValidAdvisorFirmIdPair(r.tenantId, r.firmWorkspaceId), {
    message: 'tenantId and firmWorkspaceId must both be strict-Crockford appcore ids',
  });

/** The current {@link AdvisorFirmDirectorySchema} document version. */
export const ADVISOR_FIRM_DIRECTORY_VERSION = 1;

/**
 * The published document.
 *
 * `version` is not pinned to a literal: the records are `strictObject`, so a genuinely
 * incompatible future document already fails on its records rather than on this number. It is
 * here for logs and for a human reading the parameter, and for a future consumer that wants to
 * refuse a shape it predates without having to guess.
 *
 * `generatedAt` is the publisher's clock at drain time. For the readers this file describes it
 * is NOT a freshness gate — a STALE directory is safe FOR READS (every firm in it still
 * resolves) while an EMPTY one is a fleet-wide fail-closed event, so the publisher's rule is
 * "write nothing when the appcore read fails" and the consumer's rule is "use what you have".
 * A consumer that wants to alarm on staleness has the timestamp to do it with. As of
 * 2026-10-08 ONE consumer does gate on it: the kept remover acts irreversibly on `disabled`
 * (see `status`), so it removes nothing on a firm basis from a directory older than six hours
 * (`MAX_DIRECTORY_AGE_MS` in `services/kept-remover/src/sweep.ts`). "A stale directory is safe"
 * is therefore true for reads only. ⚠️ A stale document can still name a firm deleted
 * since, so a WRITE that forms access against a firm confirms it against appcore first
 * (steward-app#1370, `services/steward-app-api/src/services/firmConfirmation.ts`).
 */
export const AdvisorFirmDirectorySchema = z
  .strictObject({
    version: z.number().int().min(1),
    /**
     * ⚠️ **UTC only.** zod 4's `z.iso.datetime()` defaults to `offset: false`, so
     * `2026-09-01T12:00:00Z` parses and `2026-09-01T12:00:00+00:00` does NOT. That is fine —
     * `Date.prototype.toISOString()` always emits the `Z` form — but a publisher that formats
     * with an offset instead produces a document nothing in the fleet can parse, which is the
     * fleet-wide outage this file's header is otherwise built to avoid. #743 PR 4: use
     * `toISOString()`.
     */
    generatedAt: z.iso.datetime(),
    firms: z.array(AdvisorFirmRecordSchema).max(500),
  })
  /**
   * Cross-record uniqueness, checked at the DOCUMENT level because no single record can see
   * its neighbours.
   *
   * `slug` is deliberately NOT here — `activeDirectoryRecords` resolves a duplicated slug by
   * taking the first, which is the registry's own long-standing rule. These three are
   * different, because a duplicate is not a tie to break but a claim on something that
   * already belongs to another firm:
   *
   *  - `firmWorkspaceId` — two firms sharing one aims firm B's submissions at firm A's
   *    workspace. Today the writer refuses that (`addressedToUs` compares the message's
   *    workspace against its own configured one), but `intakeMessage.ts` records that #743
   *    PR 7 REMOVES exactly those comparisons once both sides resolve from this document. So
   *    the check has to exist here before that one goes away.
   *  - `tenantId` — `firmForTenant` gates the whole advisor nav and takes the first match; a
   *    shared tenant makes which firm an advisor "is" depend on array order.
   *  - `hostnames` — `advisorFirmDefForHostname` takes the first match too, so a duplicate
   *    decides brand injection on an unauthenticated page by array position. It also feeds
   *    the advisor-facing intake link (`src/advisor/intakeLink.ts`), the CORS origin set and
   *    the Cognito callback set later.
   *
   * Wholesale, like every other failure here: a document making one of these claims is
   * refused entirely rather than partially honoured.
   */
  .superRefine((doc, ctx) => {
    const claim = (kind: string, values: Iterable<string>, path: string) => {
      const seen = new Set<string>();
      for (const value of values) {
        if (seen.has(value)) {
          ctx.addIssue({
            code: 'custom',
            path: ['firms', path],
            message: `two firms claim the same ${kind}`,
          });
          return;
        }
        seen.add(value);
      }
    };
    claim('tenantId', doc.firms.map((f) => f.tenantId), 'tenantId');
    claim('firmWorkspaceId', doc.firms.map((f) => f.firmWorkspaceId), 'firmWorkspaceId');
    claim('hostname', doc.firms.flatMap((f) => f.hostnames), 'hostnames');
  });

export type AdvisorFirmIntro = z.infer<typeof AdvisorFirmIntroSchema>;
export type AdvisorFirmRecord = z.infer<typeof AdvisorFirmRecordSchema>;
export type AdvisorFirmDirectory = z.infer<typeof AdvisorFirmDirectorySchema>;

/**
 * Parses a published directory document, or returns null.
 *
 * Fail closed and WHOLESALE: one malformed record makes the whole document null, and the
 * caller's `FirmLookupContext` then carries no `directory` at all. Null is a safe answer
 * everywhere — it is the same answer a fresh environment gives — but it is never a QUIET one:
 * the caller (PR 6's `services/firmDirectory.ts`) is expected to log the rejection, because a
 * directory that stopped parsing is a fleet-wide outage of the advisor channel and must be
 * visible as one.
 *
 * Takes `unknown` rather than a string so the caller decides how the bytes arrived (an S3
 * object body, a test fixture) and owns its own JSON-parse failure.
 */
export function parseAdvisorFirmDirectory(raw: unknown): AdvisorFirmDirectory | null {
  const result = AdvisorFirmDirectorySchema.safeParse(raw);
  return result.success ? result.data : null;
}

/**
 * The firms a directory actually offers: `status: 'active'` only, and at most one record per
 * slug (the FIRST wins, matching `advisorFirmNameForMailbox`'s "the first configured firm
 * listing the address wins; a duplicate is a registry mistake, not a case to resolve at
 * request time").
 *
 * `internal: true` firms are NOT filtered here. They resolve exactly like any other — the
 * intake route must answer for the demo firm, and the writer must store its submissions. The
 * filter that hides them from the SPA's firm picker is a different decision on a different
 * surface, and folding it in here would silently break the demo firm the whole rollout is
 * verified against (docs/32 §7 checklist 12).
 *
 * ⚠️ **That surface is the FAÇADE's `GET /advisor/firms`, not appcore's `GET /advisor-firms`**
 * (steward-app#1226). This paragraph used to name appcore's route (#743 PR 1), which has never
 * been one the SPA calls: `src/pages/people/Advisors.tsx` renders the picker from the
 * façade route's `items`, and until #1226 that array carried internal firms through to every
 * family. The filter that actually protects the picker is `!f.internal` on that projection in
 * `services/steward-app-api/src/handlers/advisor.ts` (`listFirms`); appcore's filter guards
 * appcore's own directory route, which is a real surface with real callers but not this one.
 */
export function activeDirectoryRecords(
  directory: AdvisorFirmDirectory | undefined,
): readonly AdvisorFirmRecord[] {
  if (!directory) return [];
  // Dedupe FIRST, across every record whatever its status, and only then drop the disabled
  // ones. Filtering before deduping would make the two orderings disagree: a disabled record
  // followed by an active twin would resolve, so `status: 'disabled'` — the kill switch for a
  // firm's unauthenticated intake page AND for its invite-throttle widening — would silently
  // do nothing. A disable that fails open is the wrong thing to get wrong, so the FIRST record
  // for a slug is the record, and if it is disabled the slug is absent.
  const bySlug = new Map<string, AdvisorFirmRecord>();
  for (const record of directory.firms) {
    if (!bySlug.has(record.slug)) bySlug.set(record.slug, record);
  }
  // A reserved slug is dropped here, one record at a time, rather than refused by
  // `AdvisorFirmRecordSchema` — read {@link RESERVED_ADVISOR_FIRM_SLUGS} before moving it:
  // this module's parse fails wholesale, so a schema-level refusal would take every OTHER
  // firm down with the offending one.
  //
  // It sits beside the `status` filter but it is NOT the same kind of rule, and an earlier
  // draft of this comment claimed it was. `status` is a property of the RECORD, so the
  // dedupe must run first or a disabled entry followed by an active twin resurrects the
  // slug (the fail-open the block above exists to prevent). Reserved-ness is a property of
  // the SLUG — the map key itself — so every record under a reserved key is dropped
  // whichever order the two steps run in. Verified by mutation: moving this test into the
  // dedupe loop reds nothing, correctly. Placed here anyway, because "one filter step, both
  // reasons" is easier to read than two, not because the order is load-bearing.
  return [...bySlug.values()]
    .filter((r) => r.status === 'active' && !isReservedAdvisorFirmSlug(r.slug));
}
